import React, { useState, useRef, useEffect } from "react";
import { Box, Flex } from "@chakra-ui/react";

interface SplitViewProps {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  isRightPanelVisible: boolean;
  defaultRightWidth?: number;
  /** When true, the right panel fills the full width and the drag handle is hidden. */
  expandRightPanel?: boolean;
}

const SplitView: React.FC<SplitViewProps> = ({
  leftPanel,
  rightPanel,
  isRightPanelVisible,
  defaultRightWidth = 50,
  expandRightPanel = false,
}) => {
  const [dividerPos, setDividerPos] = useState(defaultRightWidth);
  const isDragging = useRef(false);

  useEffect(() => {
    if (isRightPanelVisible) {
      setDividerPos(defaultRightWidth);
    }
  }, [isRightPanelVisible]);

  const startDrag = () => {
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const stopDrag = () => {
    isDragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  const onDrag = (e: MouseEvent) => {
    if (!isDragging.current) return;
    const newPos = (e.clientX / window.innerWidth) * 100;
    if (newPos > 20 && newPos < 80) setDividerPos(newPos);
  };

  useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, []);

  // Reset divider position when panel becomes visible
  useEffect(() => {
    if (isRightPanelVisible && dividerPos < 20) {
      setDividerPos(defaultRightWidth);
    }
  }, [defaultRightWidth, dividerPos]);

  const effectiveDividerPos = expandRightPanel ? 0 : dividerPos;
  const leftWidth = isRightPanelVisible ? effectiveDividerPos : 100;
  const rightWidth = isRightPanelVisible ? 100 - effectiveDividerPos : 0;

  return (
    <Flex height="100%" width="100%" position="relative" overflow="hidden">
      {/* Left Panel */}
      <Box
        width={`${leftWidth}%`}
        height="100%"
        transition={isDragging.current ? "none" : "width 0.3s ease-in-out"}
        overflow="hidden"
        position="relative"
      >
        {leftPanel}
      </Box>

      {/* Resizer Handle — hidden when the right panel is expanded full-width */}
      {isRightPanelVisible && !expandRightPanel && (
        <Box
          role="separator"
          aria-orientation="vertical"
          width="14px"
          height="100%"
          cursor="col-resize"
          position="relative"
          onMouseDown={startDrag}
          flexShrink={0}
          _hover={{
            "& [data-split-line]": { bg: "gray.400" },
            "& [data-split-handle]": { bg: "gray.500" },
          }}
        >
          <Box
            data-split-line
            position="absolute"
            left="50%"
            top={0}
            transform="translateX(-50%)"
            width="2px"
            height="100%"
            bg="gray.300"
            transition="background-color 0.2s"
          />
          <Box
            data-split-handle
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            zIndex={1}
            width="9px"
            height="27px"
            borderRadius="full"
            bg="gray.300"
            border="1px solid"
            borderColor="gray.500"
            boxShadow="sm"
            pointerEvents="none"
            transition="background-color 0.2s"
          />
        </Box>
      )}

      {/* Right Panel */}
      {isRightPanelVisible && (
        <Box
          width={`${rightWidth}%`}
          height="100%"
          transition={isDragging.current ? "none" : "width 0.3s ease-in-out"}
          overflow="hidden"
          position="relative"
          bg="white"
        >
          {rightPanel}
        </Box>
      )}
    </Flex>
  );
};

export default SplitView;
